import { useState } from "react";
import { toast } from "react-toastify";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import * as api from "../../../shared/lib/api";
import { extractApiErrorMessage } from "../../../shared/utils/extract-api-error-message";
import { ApiProps } from "../../../features/api/types/api";
import { FailureRule } from "../../../features/company/types/company-api-failure-message";
import FailureRuleBuilder from "../../../features/api/components/failure-rule-builder";

type AddFailureMessageFormProps = {
	companyId: string;
	apiOptions: ApiProps[];
	existingApiIds: string[];
	onCreated: () => void;
};

export default function AddFailureMessageForm({
	companyId,
	apiOptions,
	existingApiIds,
	onCreated,
}: AddFailureMessageFormProps): JSX.Element {
	const [newRuleApi, setNewRuleApi] = useState<string>("");
	const [newRuleRules, setNewRuleRules] = useState<FailureRule[]>([]);
	const [creating, setCreating] = useState<boolean>(false);

	async function createFailureMessage() {
		if (!newRuleApi) {
			toast.error("Selecione uma API.", { position: toast.POSITION.TOP_RIGHT });
			return;
		}

		if (newRuleRules.length === 0) {
			toast.error("Adicione pelo menos uma regra.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			return;
		}

		setCreating(true);
		const response = (await api.post("company/api-failure-message/create/", {
			company: companyId,
			api: newRuleApi,
			rules: newRuleRules,
		})) as { [key: string]: any };
		setCreating(false);

		if (response.status === 201) {
			toast.success("Mensagem de erro cadastrada com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setNewRuleApi("");
			setNewRuleRules([]);
			onCreated();
		} else {
			toast.error(extractApiErrorMessage(response), {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	return (
		<>
			<Typography
				variant="subtitle1"
				sx={{
					marginBottom: 2,
					marginTop: 0,
					color: "text.secondary",
					fontWeight: 600,
					textTransform: "uppercase",
					fontSize: "0.85rem",
				}}
			>
				Adicionar nova mensagem
			</Typography>

			<FormControl fullWidth sx={{ marginBottom: 2 }}>
				<InputLabel id="new-rule-api-label">API</InputLabel>
				<Select
					labelId="new-rule-api-label"
					label="API"
					value={newRuleApi}
					onChange={(e) => setNewRuleApi(e.target.value as string)}
				>
					{apiOptions.map((item) => {
						const alreadyConfigured = existingApiIds.includes(item.id);
						return (
							<MenuItem
								key={`new-rule-api-${item.id}`}
								value={item.id}
								disabled={alreadyConfigured}
							>
								{item.name} ({item.provider_name})
								{alreadyConfigured ? " — já configurada" : ""}
							</MenuItem>
						);
					})}
				</Select>
				<FormHelperText>
					APIs já configuradas para esta empresa ficam desabilitadas — edite a
					configuração existente em vez de criar outra.
				</FormHelperText>
			</FormControl>

			<FailureRuleBuilder rules={newRuleRules} onChange={setNewRuleRules} />

			<Button
				fullWidth
				variant="contained"
				size="large"
				color="success"
				disabled={creating}
				onClick={createFailureMessage}
				sx={{ marginTop: 2 }}
			>
				Adicionar
			</Button>
		</>
	);
}
